/* oxlint-disable typescript/no-explicit-any -- schema library requires flexible any types */
/* oxlint-disable typescript/no-unsafe-argument, typescript/no-unsafe-assignment, typescript/no-unsafe-call, typescript/no-unsafe-member-access, typescript/no-unsafe-return -- schema library uses any types throughout */
import { requiresCycleTracking } from '../core/schema-graph'
import { completeObjectProperty } from '../core/object-result'
import { BaseSchema } from '../core/base-schema'
import type * as errors from '../core/errors'
import * as types from '../core/types'
import type * as util from '../core/util'
import type { DefaultSchema } from './wrappers'
import { _default as makeDefault } from './wrappers'
import type { OptionalSchema, NullableSchema } from '../core/base-schema'

// Object shape type - use any to allow recursive types
export type Shape = Record<string, any>

// Check if schema is optional (for type inference)
type OptionalOutSchema = { _zod: { optout: 'optional' } }

// Check if shape has string index signature
type HasStringIndex<T> = string extends keyof T ? true : false

// Infer object output type
export type InferObjectOutput<T extends Shape> =
  HasStringIndex<T> extends true
    ? Record<
        string,
        T[keyof T] extends types.SomeSchema ? types.output<T[keyof T]> : unknown
      >
    : util.Prettify<
        {
          -readonly [K in keyof T as T[K] extends OptionalOutSchema
            ? never
            : K]: T[K]['_zod']['output']
        } & {
          -readonly [K in keyof T as T[K] extends OptionalOutSchema
            ? K
            : never]?: T[K]['_zod']['output']
        }
      >

// Check if schema is optional input (for type inference)
type OptionalInSchema = { _zod: { optin: 'optional' | 'defaulted' } }

// Infer object input type
export type InferObjectInput<T extends Shape> =
  HasStringIndex<T> extends true
    ? Record<
        string,
        T[keyof T] extends types.SomeSchema ? types.input<T[keyof T]> : unknown
      >
    : util.Prettify<
        {
          -readonly [K in keyof T as T[K] extends OptionalInSchema
            ? never
            : K]: T[K]['_zod']['input']
        } & {
          -readonly [K in keyof T as T[K] extends OptionalInSchema
            ? K
            : never]?: T[K]['_zod']['input']
        }
      >

// Object schema definition
export interface ObjectSchemaDef<T extends Shape> extends types.SchemaTypeDef {
  type: 'object'
  shape: T
}

export interface ObjectSchemaInternals<
  T extends Shape,
> extends types.SchemaInternals<InferObjectOutput<T>, InferObjectInput<T>> {
  def: ObjectSchemaDef<T>
}

export interface ObjectSchema<T extends Shape> extends types.Schema<
  InferObjectOutput<T>,
  InferObjectInput<T>,
  ObjectSchemaInternals<T>
> {
  readonly shape: T
  extend<U extends Shape>(extension: U): ObjectSchema<util.Extend<T, U>>
  pick<K extends keyof T>(...keys: K[]): ObjectSchema<Pick<T, K>>
  omit<K extends keyof T>(...keys: K[]): ObjectSchema<Omit<T, K>>
  partial(): ObjectSchema<{ [K in keyof T]: OptionalWrap<T[K]> }>
  optional(): OptionalSchema<this>
  nullable(): NullableSchema<this>
  default(
    defaultValue: InferObjectOutput<T> | (() => InferObjectOutput<T>),
  ): DefaultSchema<this>
  meta(metadata: Record<string, any>): this
  parse(data: unknown, params?: types.ParseContext): InferObjectOutput<T>
  safeParse(
    data: unknown,
    params?: types.ParseContext,
  ):
    | { success: true; data: InferObjectOutput<T> }
    | { success: false; error: errors.SchemaError }
}

// Helper to wrap schema in optional
type OptionalWrap<T extends types.Schema> = OptionalSchema<T>

export const ObjectSchema = types.$constructor<ObjectSchema<any>>(
  'ObjectSchema',
  (inst, def: ObjectSchemaDef<any>) => {
    BaseSchema.init(inst, def)

    // Make shape accessor lazy to support recursive types
    let normalizedShape: any
    const getShape = () => {
      normalizedShape ??= { ...def.shape }
      return normalizedShape
    }

    inst._zod.objectShape = getShape
    let recursive: boolean | undefined
    const isRecursive = () => recursive ??= requiresCycleTracking(inst)
    inst._zod.objectIsRecursive = isRecursive
    let keys: PropertyKey[] | undefined

    let active: WeakMap<types.ParseContext, WeakMap<object, Record<string, any>>> | undefined
    inst._zod.parse = (payload, ctx) => {
      if (
        typeof payload.value !== 'object' ||
        payload.value === null ||
        Array.isArray(payload.value)
      ) {
        payload.issues.push({
          code: 'invalid_type',
          expected: 'object',
          input: payload.value,
          inst,
        })
        return payload
      }

      const input = payload.value as Record<PropertyKey, any>
      let pending: WeakMap<object, Record<string, any>> | undefined
      if (isRecursive()) {
        active ??= new WeakMap()
        pending = active.get(ctx)
        if (!pending) { pending = new WeakMap(); active.set(ctx, pending) }
      }
      const previous = pending?.get(input)
      if (previous) { payload.value = previous; return payload }
      const result: Record<string, any> = {}
      pending?.set(input, result)
      const promises: Promise<any>[] = []
      try {
      const shape = getShape()

      // Process shape properties
      keys ??= Reflect.ownKeys(shape).filter(key => key !== '__proto__')
      for (const key of keys) {

        const propSchema = shape[key]
        const propValue = input[key]

        const propPayload = propSchema._zod.run(
          { value: propValue, issues: [] },
          ctx,
        )

        if (propPayload instanceof Promise) {
          promises.push(
            // oxlint-disable-next-line promise/always-return -- void side-effect callback
            propPayload.then((p) => {
              completeObjectProperty(key, propSchema, p, payload, input, result)
            }),
          )
        } else {
          completeObjectProperty(key, propSchema, propPayload, payload, input, result)
        }
      }

      if (promises.length > 0) {
        return Promise.all(promises).then(() => {
          payload.value = result
          return payload
        }).finally(() => { pending?.delete(input) })
      }

      pending?.delete(input)
      payload.value = result
      return payload
      } catch (error) { pending?.delete(input); throw error }
    }

    Object.defineProperty(inst, 'shape', {
      get: () => def.shape,
      enumerable: true,
    })

    inst.extend = (extension) => {
      return new ObjectSchema({
        type: 'object',
        shape: { ...def.shape, ...extension },
      })
    }

    inst.pick = (...keys) => {
      const picked: any = {}
      for (const key of keys) {
        picked[key] = def.shape[key]
      }
      return new ObjectSchema({
        type: 'object',
        shape: picked,
      })
    }

    inst.omit = (...keys) => {
      const omitted: any = { ...def.shape }
      for (const key of keys) {
        delete omitted[key]
      }
      return new ObjectSchema({
        type: 'object',
        shape: omitted,
      })
    }

    inst.partial = () => {
      const partial: any = {}
      for (const key in def.shape) {
        const schema = def.shape[key]
        partial[key] = schema.optional()
      }
      return new ObjectSchema({
        type: 'object',
        shape: partial,
      })
    }

    inst.default = (defaultValue: any) => {
      return makeDefault(inst, defaultValue)
    }
  },
)

export function object<T extends Shape>(
  shape: T,
): ObjectSchema<util.Writeable<T>> {
  return new ObjectSchema({
    type: 'object',
    shape: shape as any,
  }) as any
}
